import { useQuery } from "@tanstack/react-query";
import { api } from "../api";

export interface MarketDemandPoint {
  /** YYYY-MM-01 string. Monthly granularity — Keyword Planner's smallest bucket. */
  date: string;
  /** Raw monthly volumes — kept for legacy callers, not used by the chart. */
  brand: number | null;
  market: number | null;
  /** YoY % change vs same month one year ago. Null for the first 12 months
   *  of the window where prior-year data isn't yet available. Drives the
   *  dashed Market Demand line on the chart. */
  brandYoyPct: number | null;
  marketYoyPct: number | null;
}

export interface MarketDemandSeriesResponse {
  brandKeyword: string | null;
  marketKeyword: string | null;
  capturedAt: string | null;
  series: MarketDemandPoint[];
}

/**
 * Monthly Keyword Planner volumes for the customer's brand + market keywords.
 * Powers the dashed "Market Demand" line on the Trends + Breakdown chart.
 *
 * Data is updated by the daily 02:00 ET cron — 5-min staleTime is enough
 * since the underlying value only changes once per day at most.
 */
export function useMarketDemandSeries(
  userId: string | undefined,
  startDate?: string,
  endDate?: string,
) {
  return useQuery<MarketDemandSeriesResponse>({
    queryKey: ["market-demand-series", userId, startDate, endDate],
    queryFn: () =>
      api.marketing.marketDemandSeries.get(userId as string, {
        startDate,
        endDate,
      }) as Promise<MarketDemandSeriesResponse>,
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}
